/**
 * NPI Registry API client
 * Documentation: https://npiregistry.cms.hhs.gov/api-page
 */

import axios from "axios";

import { isServer } from "@/src/lib/runtime";

const NPI_REGISTRY_BASE_URL = "https://npiregistry.cms.hhs.gov/api/";
const NPI_API_VERSION = "2.1";

export const NPI_NUMBER_REGEX = /^\d{10}$/;

// API Types
export interface NPISearchParams {
  number?: string; // NPI number
  enumeration_type?: "NPI-1" | "NPI-2"; // Individual (NPI-1) or Organization (NPI-2)
  taxonomy_description?: string; // Taxonomy description
  first_name?: string; // First name
  last_name?: string; // Last name
  organization_name?: string; // Organization name
  address_purpose?: "LOCATION" | "MAILING";
  city?: string; // City
  state?: string; // State abbreviation
  postal_code?: string; // Postal code
  country_code?: string; // Country code (2 letters)
  limit?: number; // Number of results to return (max 200)
  skip?: number; // Number of results to skip
}

export interface NPIAddress {
  address_1: string;
  address_2?: string;
  city: string;
  state: string;
  postal_code: string;
  country_code: string;
  telephone_number?: string;
  fax_number?: string;
  address_purpose: "LOCATION" | "MAILING";
}

export interface NPITaxonomy {
  code: string;
  desc: string;
  primary: boolean;
  state?: string;
  license?: string;
}

export interface NPIResult {
  number: string;
  enumeration_type: "NPI-1" | "NPI-2";
  basic: {
    first_name?: string;
    last_name?: string;
    middle_name?: string;
    organization_name?: string;
    gender?: string;
    status: "A" | "I";
    name_prefix?: string;
    name_suffix?: string;
    credential?: string;
    last_updated: string;
    enumeration_date: string;
    date_of_birth: string;
  };
  taxonomies: NPITaxonomy[];
  addresses: NPIAddress[];
  other_names?: {
    organization_name?: string;
    first_name?: string;
    last_name?: string;
    type: string;
  }[];
}

export interface NPIResponse {
  result_count: number;
  results: NPIResult[];
}

/**
 * Search the NPI Registry with the given parameters
 * @param params Search parameters
 * @returns Promise with the search results
 */
export async function searchNPI(params: NPISearchParams): Promise<NPIResponse> {
  const searchParams = new URLSearchParams();

  // Add all defined parameters to the search query
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined) {
      searchParams.append(key, value.toString());
    }
  });

  try {
    let url: string;
    let headers: Record<string, string> | undefined;

    if (isServer) {
      searchParams.set("version", NPI_API_VERSION);
      url = `${NPI_REGISTRY_BASE_URL}?${searchParams.toString()}`;
      headers = { Accept: "application/json" };
    } else {
      url = `/api/npi?${searchParams.toString()}`;
    }

    const response = await axios.get<NPIResponse>(url, { headers });

    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      throw new Error(
        `NPI Registry API error: ${error.response?.status} ${error.response?.statusText || error.message}`
      );
    }

    throw error;
  }
}

/** Maximum number of states to query when performing a per-state search */
const ALLOWED_STATES_SEARCH_LIMIT = 5;

/**
 * Merge multiple NPI result arrays and deduplicate by NPI number.
 */
export function mergeNpiResultsByNumber(resultsArrays: NPIResult[][]): NPIResult[] {
  const seen = new Set();

  return resultsArrays.flat().filter((item) => !seen.has(item.number) && seen.add(item.number));
}

/**
 * Search for providers by a single ambiguous token (first name or last name).
 * Runs two requests per state (first_name=token and last_name=token), then merge/dedupe by NPI.
 *
 * @param token Search term (matched as first name or last name)
 * @param states Optional state abbreviations; when length > 1, capped at ALLOWED_STATES_SEARCH_LIMIT (5)
 * @param limit Maximum number of results per request (default 50, max 200)
 */
export async function searchProvidersByFirstOrLastName(
  token: string,
  states?: string[],
  limit: number = 50
): Promise<NPIResponse> {
  const cappedLimit = Math.min(limit, 200);
  const base = { enumeration_type: "NPI-1" as const, limit: cappedLimit };
  const statesToUse = states && states.length > 1 ? states.slice(0, ALLOWED_STATES_SEARCH_LIMIT) : states;

  if (!statesToUse || statesToUse.length === 0) {
    const [r1, r2] = await Promise.all([
      searchNPI({ ...base, first_name: token }),
      searchNPI({ ...base, last_name: token }),
    ]);
    const merged = mergeNpiResultsByNumber([r1.results ?? [], r2.results ?? []]);

    return { results: merged, result_count: merged.length };
  }

  if (statesToUse.length === 1) {
    const [r1, r2] = await Promise.all([
      searchNPI({ ...base, first_name: token, state: statesToUse[0] }),
      searchNPI({ ...base, last_name: token, state: statesToUse[0] }),
    ]);
    const merged = mergeNpiResultsByNumber([r1.results ?? [], r2.results ?? []]);

    return { results: merged, result_count: merged.length };
  }

  const responses = await Promise.all(
    statesToUse.flatMap((stateAbbr) => [
      searchNPI({ ...base, first_name: token, state: stateAbbr }),
      searchNPI({ ...base, last_name: token, state: stateAbbr }),
    ])
  );
  const merged = mergeNpiResultsByNumber(responses.map((r) => r.results ?? []));

  return { results: merged, result_count: merged.length };
}

/**
 * Search for individual providers by name, optionally scoped to a list of states.
 * When states is undefined or empty, one request with no state filter.
 * When one state, one request with that state.
 * When multiple states, one request per state then merge/dedupe by NPI (capped at ALLOWED_STATES_SEARCH_LIMIT).
 *
 * @param firstName Provider's first name
 * @param lastName Provider's last name
 * @param states Optional state abbreviations; when length > 1, capped at ALLOWED_STATES_SEARCH_LIMIT (5)
 * @param limit Maximum number of results per request (default 10, max 200)
 */
export async function searchProvidersByName(
  firstName: string,
  lastName: string,
  states?: string[],
  limit: number = 10
): Promise<NPIResponse> {
  const cappedLimit = Math.min(limit, 200);
  const statesToUse = states && states.length > 1 ? states.slice(0, ALLOWED_STATES_SEARCH_LIMIT) : states;

  if (!statesToUse || statesToUse.length === 0 || statesToUse.length === 1) {
    return searchNPI({
      first_name: firstName,
      last_name: lastName,
      ...(statesToUse?.length === 1 ? { state: statesToUse[0] } : {}),
      enumeration_type: "NPI-1",
      limit: cappedLimit,
    });
  }

  const responses = await Promise.all(
    statesToUse.map((stateAbbr) =>
      searchNPI({
        first_name: firstName,
        last_name: lastName,
        state: stateAbbr,
        enumeration_type: "NPI-1",
        limit: cappedLimit,
      })
    )
  );

  const merged = mergeNpiResultsByNumber(responses.map((r) => r.results ?? []));

  return { results: merged, result_count: merged.length };
}

/**
 * Search for individual providers by NPI number
 * @param npiNumber Provider's NPI number
 * @param limit Maximum number of results to return (default 10, max 200)
 * @returns Promise with the search results
 */
export async function searchProvidersByNPI(npiNumber: string, limit: number = 10): Promise<NPIResponse> {
  return searchNPI({
    number: npiNumber,
    enumeration_type: "NPI-1",
    limit: Math.min(limit, 200),
  });
}

/**
 * Search for organizations by name
 * @param organizationName Organization name to search for
 * @param limit Maximum number of results to return (default 10, max 200)
 * @returns Promise with the search results
 */
export async function searchOrganizations(organizationName: string, limit: number = 10): Promise<NPIResponse> {
  return searchNPI({
    organization_name: organizationName,
    enumeration_type: "NPI-2",
    limit: Math.min(limit, 200),
  });
}
